// Wires a signed-in session to: local partitions → view-model controller → offline workspace ports → sync engines.
// Unified view: every workspace of the account is loaded at once (own projects and projects shared with you side
// by side). Each workspace keeps its own IndexedDB partition, outbox, file queue and sync engine (tenant isolation
// and the sync protocol are unchanged); one composite port routes each edit to the workspace owning the project.
import { reactive, toRaw, watchEffect } from 'vue';
import type { Desks, Pane, Project, User } from '@/domain/model';
import { shareFrom } from './structuralShare';
import { PanesController } from '@/modules/panes/controller';
import { localPrefs, type OnlinePort, type TrashedProject, type WorkspacePort } from '@/modules/panes/ports';
import { openLocalDb, partitionName } from '@/db/localDb';
import { WorkspaceStore } from '@/db/workspaceStore';
import { offlineWorkspace } from './offlineWorkspace';
import { SyncEngine, newSyncStatus, syncStatus, type SyncStatus } from '@/sync/engine';
import { httpSyncTransport } from '@/api/syncTransport';
import { logout, me as fetchMe } from '@/api/auth';
import { forgetSession, rememberSession, type SessionInfo } from './session';
import { forgetPush, initPush } from './push';
import { uid } from '@/modules/panes/constants';
import { tpCal, tpLoc } from '@/modules/panes/calendar';
import { requestPersistentStorage } from '@/pwa/register';
import { ViewSync } from './viewSync';
import { PrefsSync } from './prefsSync';
import { brand } from './brand';
import { requestSignOut } from './signOutGuard';
import { deletePartition } from '@/db/localDb';
import { downloadText } from './download';
import { FileQueue } from '@/sync/files';
import { httpFileTransport } from '@/api/fileTransport';
import { uuidv7 } from './ids';
import { api } from '@/api/client';
import { TransportError } from './ports';
import { t as tr } from './i18n';
import { httpOnlinePort } from '@/api/sharing';
import type { MetaUser } from '@/sync/engine';
import { httpMarkdownApi, type MarkdownApi } from '@/modules/markdown/api';
import { deleteDraftPartition, idbDraftStore } from '@/modules/markdown/draftStore';
import { forEachMarkdownSession } from '@/modules/markdown/session';
import type { Connectivity } from './ports';
import { startRealtime } from '@/realtime/client';

export interface Runtime { ctrl: PanesController; dispose(): void }

const COLORS = ['#5b8def', '#d9895b', '#a678e0', '#4fb286', '#d46a8c', '#c9a64a'];

function shareError(e: unknown): string {
  const code = e instanceof TransportError ? e.code : '';
  if (e instanceof TransportError && e.kind === 'network') return tr('This action needs a connection');
  if (code === 'forbidden' || code === 'project_owner_required') return tr('Only the project owner can change sharing');
  if (code === 'validation_failed') return tr('Enter an email or @username');
  if (code === 'last_owner') return tr('A project needs at least one owner');
  if (code === 'already_member') return tr('Already a member');
  return tr('Could not update sharing');
}

type OfflinePort = ReturnType<typeof offlineWorkspace>;
/** One workspace of the account: its own partition, port, engine and online endpoints. */
interface Unit {
  id: string; role: string;
  partition: { brandId: string; userId: string; workspaceId: string };
  db: Awaited<ReturnType<typeof openLocalDb>>; store: WorkspaceStore; port: OfflinePort; engine: SyncEngine; files: FileQueue;
  online: OnlinePort; md: MarkdownApi; status: SyncStatus; channel: BroadcastChannel | null;
  projects: Project[]; users: MetaUser[];
}

/** Several engines → one indicator: the most urgent state wins; pending counts add up; "last synced" is the oldest.
 *  (one engine that cannot reach the server means the device is offline, even while another one is attempting) */
const RANK: Record<SyncStatus['state'], number> = { action_required: 5, offline: 4, syncing: 3, pending: 2, saved_on_device: 1, synced: 0 };
function aggregate(list: SyncStatus[]): void {
  if (!list.length) return;
  const top = list.reduce((a, b) => (RANK[b.state] > RANK[a.state] ? b : a));
  syncStatus.state = top.state;
  syncStatus.detail = top.detail;
  syncStatus.pending = list.reduce((n, x) => n + x.pending, 0);
  syncStatus.conflicts = list.reduce((n, x) => n + x.conflicts, 0);
  syncStatus.lastSyncAt = list.every(x => x.lastSyncAt > 0) ? Math.min(...list.map(x => x.lastSyncAt)) : 0;
}

export async function startWorkspace(s: SessionInfo, onSignedOut: () => void): Promise<Runtime> {
  void requestPersistentStorage();
  // The home workspace holds the personal layout and receives new projects: your own (owner) workspace.
  const all = s.workspaces.length ? s.workspaces : [{ id: s.workspaceId, name: '', role: 'owner' }];
  const homeId = (all.find(w => w.role === 'owner') ?? all.find(w => w.id === s.workspaceId) ?? all[0]!).id;
  const order = [homeId, ...all.map(w => w.id).filter(id => id !== homeId)];

  const meUser: User = { id: s.userId, name: s.displayName, email: s.email, color: s.color, username: s.username ?? null, ...(s.photo ? { photo: s.photo } : {}) };
  const usersFrom = (list: MetaUser[]): User[] => {
    const m = new Map<string, User>(list.map((u, i) => [u.id, { id: u.id, name: u.name, email: u.email, color: u.color || COLORS[i % COLORS.length]!, username: u.username ?? null, ...(u.photo ? { photo: u.photo } : {}) }]));
    // My own photo: the latest local change (upload/remove) wins over a member list that has not caught up yet.
    const mine = { ...meUser, ...(m.get(meUser.id) || {}), name: meUser.name, email: meUser.email, username: meUser.username ?? m.get(meUser.id)?.username ?? null };
    if (meUser.photo) mine.photo = meUser.photo; else delete mine.photo;
    m.set(meUser.id, mine);
    return [...m.values()];
  };

  let ctrl!: PanesController;
  const units: Unit[] = [];
  const projUnit = new Map<string, Unit>();   // project id → owning workspace (loaded projects)
  const trashUnit = new Map<string, Unit>();  // project id → workspace (projects in the server Trash)
  const merged = (): Project[] => units.flatMap(u => u.projects);
  const allUsers = (): MetaUser[] => { const m = new Map<string, MetaUser>(); for (const u of units) for (const x of u.users) m.set(x.id, x); return [...m.values()]; };
  const index = () => { projUnit.clear(); for (const u of units) for (const p of u.projects) projUnit.set(p.id, u); };
  // Unchanged projects/tasks/people keep their objects, so the view only re-renders what actually changed.
  const show = () => {
    index();
    if (!ctrl) return;
    const users = usersFrom(allUsers()), prevUsers = toRaw(ctrl.state.users) as User[];
    ctrl.setState({ projects: shareFrom(toRaw(ctrl.state.projects) as Project[], merged()), users: JSON.stringify(users) === JSON.stringify(prevUsers) ? prevUsers : users });
  };
  const unitOfProject = (pid: string): Unit | undefined => projUnit.get(pid) ?? trashUnit.get(pid);
  const unitOfTask = (taskId: string): Unit | undefined => {
    for (const p of (ctrl?.state.projects as Project[] | undefined) ?? merged()) {
      if (p.lists.some(l => l.tasks.some(t => t.id === taskId)) || (p.trash || []).some(e => e.task.id === taskId)) return unitOfProject(p.id);
    }
    return undefined;
  };
  const unitOfFile = (fileId: string): Unit | undefined => {
    for (const p of (ctrl?.state.projects as Project[] | undefined) ?? merged()) {
      for (const l of p.lists) for (const t of l.tasks) if ((t.files || []).some(f => f.id === fileId)) return unitOfProject(p.id);
    }
    return undefined;
  };

  // Shared reactions to "server data arrived" (debounced across engines, at most once per AFTER_MS). Not after my
  // own pushes: while typing, every acknowledged batch used to fire five extra requests and the realtime echo of
  // that same edit fired them again, which kept the app busy and made typing lag.
  const AFTER_MS = 5_000;
  let sharedT: ReturnType<typeof setTimeout> | null = null, lastAfter = 0;
  const afterApplied = () => {
    if (sharedT) return;
    sharedT = setTimeout(() => {
      sharedT = null; lastAfter = Date.now();
      forEachMarkdownSession(m => { const st = m.state.status; if (st === 'offline' || st === 'auth') void m.retry(); else void m.refreshRemote(); });
      void adoptViews(); void adoptPrefs(); void ctrl.refreshServer();
    }, Math.max(50, lastAfter + AFTER_MS - Date.now()));
  };

  const reloadUnit = async (u: Unit, list: MetaUser[]): Promise<void> => {
    u.users = list;
    for (let i = 0; i < 5; i++) {
      // Generation first, then wait: an edit queued while we wait is either awaited here or changes the generation.
      // (Reading it after the wait missed an edit queued in between: the reload showed the stored copy without that
      // keystroke and the next render wrote it into the field — typed letters vanished while colleagues' changes arrived.)
      const g = u.port.generation();
      await u.port.flushed();
      const fresh = await u.store.loadProjects();
      if (g !== u.port.generation()) continue; // a local edit raced the reload; try again after it commits
      u.port.rebase(fresh);
      const gone = new Set(u.projects.map(p => p.id).filter(id => !fresh.some(f => f.id === id)));
      u.projects = fresh;
      show();
      // Projects this device lost (moved to Trash by the owner, access revoked): close the panes showing them.
      if (gone.size && (ctrl.state.panes as Pane[]).some(p => p.projectId && gone.has(p.projectId))) ctrl.setPanes(ps => ps.filter(p => !(p.projectId && gone.has(p.projectId))));
      return;
    }
  };
  const reloaders = new Map<Unit, (list: MetaUser[]) => void>();
  const serialReload = (u: Unit) => {
    let busy = false, again: MetaUser[] | null = null;
    const run = async (list: MetaUser[]) => {
      if (busy) { again = list; return; }
      busy = true;
      try { await reloadUnit(u, list); } finally { busy = false; if (again) { const l = again; again = null; void run(l); } }
    };
    return (list: MetaUser[]) => void run(list);
  };

  const openUnit = async (wsId: string, role: string): Promise<Unit> => {
    const partition = { brandId: s.brandId, userId: s.userId, workspaceId: wsId };
    const db = await openLocalDb(partition);
    const store = new WorkspaceStore(db);
    // Other tabs of the same partition write the same IndexedDB. Each tab must reload its projection AND its
    // durable diff base when another tab commits, or its next edit would be diffed against stale data.
    const channel: BroadcastChannel | null = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('tp-tabs:' + partitionName(partition)) : null;
    const u = { id: wsId, role, partition, db, store, channel, status: reactive(newSyncStatus()), projects: await store.loadProjects(), users: (await store.getMeta<MetaUser[]>('users')) || [],
      online: httpOnlinePort(wsId), md: httpMarkdownApi(wsId) } as Unit;
    const reload = serialReload(u); reloaders.set(u, reload);
    // Attachments: bytes are queued on the device and uploaded by the sync cycle (P12).
    u.files = new FileQueue({
      db, workspaceId: wsId, transport: httpFileTransport, newId: uuidv7,
      onChanged: () => { reload(u.users); channel?.postMessage({ type: 'changed' }); u.engine?.notifyLocalChange(); },
    });
    u.port = offlineWorkspace({
      files: u.files, store, workspaceId: wsId, deviceId: s.deviceId,
      accounts: () => [meUser],
      onRevert: ps => { u.projects = ps; show(); },
      onCommitted: () => { u.engine.notifyLocalChange(); channel?.postMessage({ type: 'changed' }); },
    });
    u.engine = new SyncEngine({
      db, transport: httpSyncTransport, workspaceId: wsId, deviceId: s.deviceId, userId: s.userId, status: u.status,
      lockName: 'tp-sync:' + partitionName(partition),
      onApplied: (list, from) => { reload(list); channel?.postMessage({ type: 'changed' }); if (from === 'pull') afterApplied(); },
      onAuthRequired: () => onSignedOut(),
      files: u.files,
      formatDate: iso => new Date(iso + 'T00:00').toLocaleDateString(tpLoc(), { calendar: tpCal(), month: 'short', day: 'numeric', year: 'numeric' }),
    });
    if (channel) channel.onmessage = async e => {
      if ((e.data as { type?: string })?.type !== 'changed') return;
      reload((await store.getMeta<MetaUser[]>('users')) || []);
      void u.engine.refreshPending();
    };
    return u;
  };
  for (const id of order) units.push(await openUnit(id, all.find(w => w.id === id)?.role ?? 'guest'));
  const home = units[0]!;
  index();
  const stopAgg = watchEffect(() => aggregate(units.map(u => u.status)));

  const savedPanes = (await home.store.getMeta<Pane[]>('panes')) || null;
  const savedDesks = (await home.store.getMeta<Desks>('desks')) || null;
  const first = merged()[0];
  const panes: Pane[] = savedPanes && savedPanes.some(p => p.type === 'projects') ? savedPanes : [{ id: 'pp', type: 'projects' }, ...(first ? [{ id: uid(), projectId: first.id, filter: 'All' }] : [])];

  const syncAll = () => Promise.all(units.map(u => u.engine.retryNow())).then(() => undefined);
  const pendingAll = () => Promise.all(units.map(u => u.engine.refreshPending())).then(ns => ns.reduce((a, b) => a + b, 0));

  // ── Online REST, routed to the workspace that owns the project ────────────────────────────────────────────────
  const route = (pid: string): OnlinePort => (unitOfProject(pid) ?? home).online;
  const online: OnlinePort = {
    sharing: pid => route(pid).sharing(pid),
    invite: (pid, who, role) => route(pid).invite(pid, who, role),
    cancelInvite: (pid, inv) => route(pid).cancelInvite(pid, inv),
    links: pid => route(pid).links(pid),
    createLink: (pid, scope) => route(pid).createLink(pid, scope),
    revokeLink: (pid, link) => route(pid).revokeLink(pid, link),
    inbox: () => home.online.inbox(),
    acceptInvite: id => home.online.acceptInvite(id),
    declineInvite: id => home.online.declineInvite(id),
    // Guests may not list another workspace's Trash: such a workspace contributes nothing.
    trashedProjects: async () => (await Promise.all(units.map(async u => {
      const list: TrashedProject[] = await u.online.trashedProjects().catch(() => []);
      for (const p of list) trashUnit.set(p.id, u);
      return list;
    }))).flat(),
    purgeProject: pid => route(pid).purgeProject(pid),
    updateProfile: patch => home.online.updateProfile(patch),
    uploadAvatar: body => home.online.uploadAvatar(body),
    removeAvatar: () => home.online.removeAvatar(),
  };

  // ── Composite port ────────────────────────────────────────────────────────────────────────────────────────────
  const taskHome = (ps: Project[]): Map<string, string> => {
    const m = new Map<string, string>();
    for (const p of ps) { for (const l of p.lists) for (const t of l.tasks) m.set(t.id, p.id); for (const e of p.trash || []) m.set(e.task.id, p.id); }
    return m;
  };
  const ownerOf = (p: Project): Unit => projUnit.get(p.id) ?? home;
  type Handle = Array<{ u: Unit; h: unknown }>;
  let nameT: ReturnType<typeof setTimeout> | null = null;
  const port: WorkspacePort = {
    commit(before, after, actorId, meta) {
      // A task cannot move between workspaces (different tenants): refuse the edit instead of delete + re-create.
      const b = taskHome(before), a = taskHome(after);
      for (const [tid, pid] of a) {
        const was = b.get(tid);
        if (was && was !== pid && (projUnit.get(was) ?? home) !== (projUnit.get(pid) ?? home)) {
          queueMicrotask(() => { show(); ctrl.flash(tr('Tasks can’t be moved to a project of another workspace')); });
          return [] as Handle;
        }
      }
      for (const p of after) if (!projUnit.has(p.id)) projUnit.set(p.id, home); // new projects go to your own workspace
      const out: Handle = [];
      for (const u of units) {
        const ub = before.filter(p => ownerOf(p) === u), ua = after.filter(p => ownerOf(p) === u);
        if (ub.length === ua.length && ub.every((p, i) => p === ua[i])) continue; // untouched (same objects)
        if (JSON.stringify(ub) === JSON.stringify(ua)) continue;
        out.push({ u, h: u.port.commit(ub, ua, actorId, meta) });
        u.projects = JSON.parse(JSON.stringify(ua)) as Project[];
      }
      return out;
    },
    undo(current, handles, actorId) {
      let out = current;
      for (const hs of [...(handles as Handle[])].reverse()) for (const { u, h } of [...hs].reverse()) out = u.port.undo(out, [h], actorId);
      return out;
    },
    saveLayout: (ps: Pane[], d?: Desks) => views.save(ps, d),
    // Display name (Profile): online PATCH /auth/me, debounced while typing; the last value wins.
    saveProfile: patch => {
      if (patch.name === undefined) return;
      const v = patch.name.trim();
      if (nameT) clearTimeout(nameT);
      nameT = setTimeout(() => {
        if (!v || v === meUser.name) return;
        void home.online.updateProfile({ display_name: v }).then(u => { meUser.name = u.display_name; }).catch(e => { ctrl.flash(e instanceof TransportError && e.kind === 'network' ? tr('This action needs a connection') : tr('Could not save your name')); });
      }, 600);
      meUser.name = v || meUser.name;
    },
    changePassword: (c, n) => home.port.changePassword(c, n),
    // Project sharing is online-only (P13). The membership itself reaches every device through sync.
    // Sharing by e-mail or @username always creates a pending invitation (design v6 F7, ADR 0011 decision 3).
    inviteToProject: async (projectId, invitee) => {
      try { await route(projectId).invite(projectId, invitee, 'editor'); return { ok: true }; }
      catch (e) { return { ok: false, error: shareError(e) }; }
    },
    removeProjectMember: async (projectId, userId) => {
      const u = unitOfProject(projectId) ?? home;
      try { await api('DELETE', `/workspaces/${u.id}/projects/${projectId}/members/${userId}`); return { ok: true }; }
      catch (e) { return { ok: false, error: shareError(e) }; }
    },
    // v10.1: `pt-{projectId}` (a project's comment thread) belongs to the project's workspace.
    attachFile: (taskId, file, opts) => ((taskId.startsWith('pt-') ? unitOfProject(taskId.slice(3)) : unitOfTask(taskId)) ?? home).port.attachFile(taskId, file, opts),
    removeFile: (taskId, fileId) => (unitOfTask(taskId) ?? home).port.removeFile!(taskId, fileId),
    projectCommand: (projectId, command, baseRevision) => (unitOfProject(projectId) ?? home).port.projectCommand!(projectId, command, baseRevision),
    accounts: () => [meUser],
    switchAccount: () => undefined,
    signOut: () => {
      const finish = async () => { await forgetPush(); await logout().catch(() => undefined); await forgetSession(); onSignedOut(); };
      void (async () => requestSignOut(await pendingAll(), {
        sync: async () => { await syncAll(); return pendingAll(); },
        exportWork: async () => {
          const parts = await Promise.all(units.map(async u => ({ workspace_id: u.id, unsynced: JSON.parse(await u.store.exportUnsynced()) as unknown })));
          downloadText(`taskpanes-unsynced-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(parts, null, 2));
        },
        discardAndSignOut: async () => {
          for (const u of units) { u.engine.stop(); u.db.close(); await deletePartition(u.partition); await deleteDraftPartition(u.partition).catch(() => undefined); }
          await finish();
        },
        signOut: finish,
      }))();
    },
    newId: uuidv7,
    api: online,
    workspaceId: home.id,
    workspaces: s.workspaces,
    hasWorkspace: id => units.some(u => u.id === id),
    syncNow: syncAll,
    // An invite accepted into a workspace this device has not loaded yet: start again (the session lists it now).
    openWorkspace: (path: string) => { location.assign(path); },
    rememberProfile: patch => {
      if (patch.username !== undefined) meUser.username = patch.username;
      if (patch.photo !== undefined) { meUser.photo = patch.photo || undefined; void rememberSession({ ...s, displayName: meUser.name, username: meUser.username, photo: patch.photo ?? null }); }
    },
  };
  // Markdown attachments (v6 F14–F16): content is online (REST, routed by file); unsaved drafts stay in the home
  // partition's draft DB and are retried when the browser reports a connection or a sync cycle succeeds.
  const mdConnectivity: Connectivity = {
    probe: async () => { try { await api('GET', '/auth/csrf'); return true; } catch { return false; } },
    onChange: cb => { const on = () => cb(true), off = () => cb(false); window.addEventListener('online', on); window.addEventListener('offline', off); return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); }; },
  };
  const mdOf = (fileId: string) => (unitOfFile(fileId) ?? home).md;
  port.markdown = {
    api: {
      getContent: id => mdOf(id).getContent(id),
      putContent: (id, c, v) => mdOf(id).putContent(id, c, v),
      listVersions: id => mdOf(id).listVersions(id),
      getVersion: (id, n) => mdOf(id).getVersion(id, n),
      restoreVersion: (id, n, v) => mdOf(id).restoreVersion(id, n, v),
    },
    store: idbDraftStore(home.partition), connectivity: mdConnectivity,
  };

  const views = new ViewSync(home.store, home.id, s.deviceId);
  const prefs = new PrefsSync(localPrefs);
  ctrl = new PanesController({ accent: brand.accent, theme: brand.theme as never }, port, prefs, { projects: merged(), panes, users: usersFrom(allUsers()), me: s.userId, desks: savedDesks });
  const adoptPrefs = async () => {
    const p = await prefs.pull(); if (!p) return;
    let pf: Record<string, unknown> = {}; try { pf = JSON.parse(localPrefs.get('taskpanes-v3-prefs') || '{}'); } catch { /* ignore */ }
    ctrl.setState({ theme: p.theme ?? ctrl.state.theme, density: p.density ?? ctrl.state.density, accent: p.accent ?? ctrl.state.accent, mobileLayout: p.mobileLayout ?? ctrl.state.mobileLayout, prefs: pf, mentionRead: Math.max(ctrl.state.mentionRead || 0, p.mentionsReadAt || 0), notifRead: Math.max(ctrl.state.notifRead || 0, p.notificationsReadAt || 0) });
  };
  void adoptPrefs();
  const adoptViews = async () => { const r = await views.pull(); if (r && r.panes.some(p => p.type === 'projects')) ctrl.setState({ panes: r.panes, ...(r.desks ? { desks: r.desks } : {}) }); };
  void adoptViews();
  for (const u of units) u.engine.start();
  // Realtime (ADR 0013): a change committed anywhere makes the owning workspace's engine pull right away; the
  // engines' own polling stays as the safety net.
  const rt = startRealtime(s.userId, units.map(u => u.id), {
    onWorkspace: (id, seq) => { units.find(u => u.id === id)?.engine.remoteChanged(seq); }, // skipped when already pulled (e.g. the echo of my own edit)
    onInbox: () => { void ctrl.refreshServer(); },
    onAuth: () => { void units[0]?.engine.sync(); }, // the engine detects the expired session and asks to sign in
  });

  // Invites inbox and the project Trash live on the server (online-only): refresh periodically and on reconnect.
  // Booted from the session cached on this device (server unreachable at start): once the server answers, adopt the
  // current profile (username, name) and refresh the cached session, so a stale cache does not linger until reload.
  let profileStale = s.offline;
  const refreshProfile = async () => {
    if (!profileStale) return;
    try {
      const m = await fetchMe();
      if (m.user.id !== meUser.id) return;
      profileStale = false;
      meUser.name = m.user.display_name; meUser.username = m.user.username ?? null; meUser.photo = m.user.photo_url || undefined;
      ctrl.setState(st => ({ users: (st.users as User[]).map(u => (u.id === meUser.id ? { ...u, name: meUser.name, username: meUser.username, photo: meUser.photo } : u)) }));
      await rememberSession({ ...s, displayName: meUser.name, username: meUser.username, photo: m.user.photo_url ?? null, offline: false });
    } catch { /* still unreachable: retried on the next poll / reconnect */ }
  };
  void ctrl.refreshServer(); void refreshProfile();
  // Deep link of this page load (e-mail / push), then clicks on push notifications while the app is open (ADR 0015).
  // (the address then keeps only the workspace part, so a reload does not reopen the same task)
  requestAnimationFrame(() => { if (ctrl.openLink(location.pathname + location.search)) history.replaceState(null, '', /^\/w\/[0-9a-f-]{36}/.exec(location.pathname)?.[0] || '/'); });
  const onSwMessage = (ev: MessageEvent) => {
    const d = ev.data as { type?: string; url?: string } | null;
    if (!d || d.type !== 'tp-open' || typeof d.url !== 'string') return;
    const ws = /^\/w\/([0-9a-f-]{36})/.exec(d.url)?.[1];
    if (ws && !units.some(u => u.id === ws)) { location.assign(d.url); return; } // a workspace this view has not loaded
    if (!ctrl.openLink(d.url)) location.assign(d.url);
  };
  if ('serviceWorker' in navigator) navigator.serviceWorker.addEventListener('message', onSwMessage);
  void initPush();
  const serverPoll = setInterval(() => { void ctrl.refreshServer(); void refreshProfile(); }, 30_000);
  const onOnline = () => { void ctrl.refreshServer(); void refreshProfile(); };
  window.addEventListener('online', onOnline);
  return {
    ctrl,
    dispose: () => {
      rt.stop(); if (sharedT) clearTimeout(sharedT); stopAgg(); clearInterval(serverPoll); window.removeEventListener('online', onOnline);
      if ('serviceWorker' in navigator) navigator.serviceWorker.removeEventListener('message', onSwMessage);
      for (const u of units) { u.engine.stop(); u.channel?.close(); u.db.close(); }
    },
  };
}
